import React, { useEffect, useMemo, useState } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useResizableColumns } from "./useResizableColumns";
import { SupportSortableHeader } from "../SupportSortableHeader";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE } from "../../components/TablePagination";
import "../../assets/css/DigixLeads.css";

const TEST_CASES_ROWS_PER_PAGE_KEY = "testCasesRowsPerPage";

function formatDateTime(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? "—"
      : d.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "—";
  }
}

function normalize(tc) {
  return {
    id: tc.Test_Case_Id ?? tc.test_case_id ?? tc.id,
    title: tc.Title ?? tc.title ?? "—",
    description: tc.Description ?? tc.description ?? "—",
    status: tc.Status ?? tc.status ?? "—",
    priority: tc.Priority ?? tc.priority ?? "—",
    preConditions: tc.Pre_Conditions ?? tc.pre_conditions ?? "—",
    steps: tc.Steps ?? tc.steps ?? "—",
    task: tc.Task_Title ?? tc.Task_Id ?? "—",
    subTask: tc.Sub_Task_Title ?? tc.Sub_Task_Id ?? "—",
    createdOn: tc.Created_On ?? tc.created_on ?? null,
    createdBy: tc.Created_By_Name ?? tc.Created_By ?? "—",
    executedOn: tc.Executed_On ?? tc.executed_on ?? null,
    executedBy: tc.Executed_By_Name ?? tc.Executed_By ?? tc.executed_by ?? "—",
    expected: tc.Expected_Result ?? "—",
    actual: tc.Actual_Result ?? "—",
    comments: tc.Comments ?? tc.comments ?? "—"
  };
}

export default function TestCasesTable({ testCases = [], onViewTestCase, onEditTestCase, onDeleteTestCase, canEdit = true, canDelete = true }) {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(TEST_CASES_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_ROWS_PER_PAGE;
  });
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const defaultWidths = [240, 260, 120, 110, 220, 320, 160, 200, 140, 160, 140, 160, 260, 260, 220, 120];
  const { getColProps, ResizeHandle } = useResizableColumns(defaultWidths);
  const rows = useMemo(() => (testCases || []).map(normalize), [testCases]);
  const getTestCaseSortValue = (tc, columnId) => {
    const dateValue = (v) => {
      if (!v) return 0;
      const ts = new Date(v).getTime();
      return Number.isFinite(ts) ? ts : 0;
    };
    switch (columnId) {
      case "title":
        return String(tc?.title ?? "").toLowerCase();
      case "description":
        return String(tc?.description ?? "").toLowerCase();
      case "status":
        return String(tc?.status ?? "").toLowerCase();
      case "priority":
        return String(tc?.priority ?? "").toLowerCase();
      case "preConditions":
        return String(tc?.preConditions ?? "").toLowerCase();
      case "steps":
        return String(tc?.steps ?? "").toLowerCase();
      case "task":
        return String(tc?.task ?? "").toLowerCase();
      case "subTask":
        return String(tc?.subTask ?? "").toLowerCase();
      case "createdOn":
        return dateValue(tc?.createdOn);
      case "createdBy":
        return String(tc?.createdBy ?? "").toLowerCase();
      case "executedOn":
        return dateValue(tc?.executedOn);
      case "executedBy":
        return String(tc?.executedBy ?? "").toLowerCase();
      case "expected":
        return String(tc?.expected ?? "").toLowerCase();
      case "actual":
        return String(tc?.actual ?? "").toLowerCase();
      case "comments":
        return String(tc?.comments ?? "").toLowerCase();
      default:
        return "";
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortColumn) return rows;
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
      const va = getTestCaseSortValue(a, sortColumn);
      const vb = getTestCaseSortValue(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return (Number(a?.id ?? 0) - Number(b?.id ?? 0)) * mult;
    });
  }, [rows, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const current = sortedRows.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [page, totalPages, sortedRows.length]);

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setPage(1);
    localStorage.setItem(TEST_CASES_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleSort = (columnId) => {
    setPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  return (
    <div className="projects-container" style={{ padding: 0, backgroundColor: "transparent", minHeight: "auto" }}>
      <div className="table-wrapper" style={{ marginBottom: 16 }}>
        <div className="table-container">
          <table className="data-table projects-table-frozen" style={{ minWidth: 1200 }}>
            <thead>
              <tr>
                <th className="col-sticky col-0 projects-th-sortable" {...getColProps(0)}><div className="projects-th-sort-row"><SupportSortableHeader columnId="title" label="Title" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={0} /></th>
                <th className="projects-th-sortable" {...getColProps(1)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="description" label="Description" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={1} /></th>
                <th className="projects-th-sortable" {...getColProps(2)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={2} /></th>
                <th className="projects-th-sortable" {...getColProps(3)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="priority" label="Priority" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={3} /></th>
                <th className="projects-th-sortable" {...getColProps(4)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="preConditions" label="Pre conditions" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={4} /></th>
                <th className="projects-th-sortable" {...getColProps(5)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="steps" label="Steps" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={5} /></th>
                <th className="projects-th-sortable" {...getColProps(6)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="task" label="Task" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={6} /></th>
                <th className="projects-th-sortable" {...getColProps(7)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="subTask" label="Sub task" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={7} /></th>
                <th className="projects-th-sortable" {...getColProps(8)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="createdOn" label="Created on" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={8} /></th>
                <th className="projects-th-sortable" {...getColProps(9)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="createdBy" label="Created by" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={9} /></th>
                <th className="projects-th-sortable" {...getColProps(10)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="executedOn" label="Executed on" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={10} /></th>
                <th className="projects-th-sortable" {...getColProps(11)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="executedBy" label="Executed by" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={11} /></th>
                <th className="projects-th-sortable" {...getColProps(12)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="expected" label="Expected" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={12} /></th>
                <th className="projects-th-sortable" {...getColProps(13)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="actual" label="Actual" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={13} /></th>
                <th className="projects-th-sortable" {...getColProps(14)}><div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="comments" label="Comments" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div><ResizeHandle index={14} /></th>
                {(onEditTestCase || onDeleteTestCase) ? (
                  <th className="col-sticky col-action" {...getColProps(15)}>Actions</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={(onEditTestCase || onDeleteTestCase) ? 16 : 15} style={{ textAlign: "center", padding: "24px", color: "#5E6C84" }}>
                    No test cases yet.
                  </td>
                </tr>
              ) : (
                current.map((r) => (
                  <tr key={r.id ?? r.title}>
                    <td className="col-sticky col-0" style={{ textAlign: "left" }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: onViewTestCase ? "#3182CE" : "#172B4D",
                          cursor: onViewTestCase ? "pointer" : "default",
                          textDecoration: onViewTestCase ? "underline" : "none"
                        }}
                        onClick={() => onViewTestCase && r.id != null && onViewTestCase(r.id)}
                        onKeyDown={(e) => onViewTestCase && r.id != null && (e.key === "Enter" || e.key === " ") && onViewTestCase(r.id)}
                        role={onViewTestCase ? "button" : undefined}
                        tabIndex={onViewTestCase ? 0 : undefined}
                      >
                        {r.title}
                      </div>
                    </td>
                    <td style={{ textAlign: "left" }}>{r.description}</td>
                    <td>
                      <span className={`status-badge status-${String(r.status || "not-set").toLowerCase().replace(/\s/g, "-")}`}>
                        {r.status}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${String(r.priority || "not-set").toLowerCase().replace(/\s/g, "-")}`}>
                        {r.priority}
                      </span>
                    </td>
                    <td style={{ textAlign: "left" }}>{r.preConditions}</td>
                    <td style={{ textAlign: "left" }}>{r.steps}</td>
                    <td style={{ textAlign: "left" }}>{r.task}</td>
                    <td style={{ textAlign: "left" }}>{r.subTask}</td>
                    <td>{formatDateTime(r.createdOn)}</td>
                    <td style={{ textAlign: "left" }}>{r.createdBy}</td>
                    <td>{formatDateTime(r.executedOn)}</td>
                    <td style={{ textAlign: "left" }}>{r.executedBy}</td>
                    <td style={{ textAlign: "left" }}>{r.expected}</td>
                    <td style={{ textAlign: "left" }}>{r.actual}</td>
                    <td style={{ textAlign: "left" }}>{r.comments}</td>
                    {(onEditTestCase || onDeleteTestCase) ? (
                      <td className="col-sticky col-action">
                        <div className="project-actions-cell">
                          {onEditTestCase && canEdit ? (
                            <button
                              type="button"
                              className="project-action-btn project-action-btn-icon project-action-btn-edit"
                              title="Edit test case"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (r.id != null) onEditTestCase(r.id);
                              }}
                            >
                              <FaEdit />
                            </button>
                          ) : null}
                          {onDeleteTestCase && canDelete ? (
                            <button
                              type="button"
                              className="project-action-btn project-action-btn-icon project-action-btn-deactivate"
                              title="Delete test case"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (r.id != null) onDeleteTestCase(r.id);
                              }}
                            >
                              <FaTrash />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {sortedRows.length > 0 && (
        <TablePagination
          currentPage={safePage}
          totalItems={sortedRows.length}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          itemLabel="test cases"
          rowsPerPageSelectId="test-cases-rows-per-page"
        />
      )}
    </div>
  );
}

